import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(identifier.trim(), password);
      if (user.role !== "shareholder") {
        toast.error("Esta entrada es exclusiva para accionistas");
        setLoading(false);
        return;
      }
      toast.success(`Bienvenido, ${user.name}`);
      navigate("/accionista");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#0A2540] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div
          aria-hidden
          className="absolute inset-0 opacity-50"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <Link to="/" className="relative flex items-center gap-3">
          <div className="h-9 w-9 rounded-sm bg-[#B4975A] flex items-center justify-center">
            <span className="font-heading text-[#0A2540] text-lg font-bold">S</span>
          </div>
          <div>
            <div className="font-heading text-lg font-bold">SmartNation</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/60">
              Portal de Accionistas
            </div>
          </div>
        </Link>

        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50">
            Acceso seguro
          </div>
          <h2 className="mt-4 font-heading text-4xl font-bold leading-tight">
            Su información,
            <br />
            <span className="text-[#B4975A]">protegida y a su alcance.</span>
          </h2>
          <p className="mt-6 text-white/70 max-w-md">
            Ingrese con su RUT y contraseña para acceder a su cartera, comunicados y más.
          </p>
        </div>

        <div className="relative text-xs text-white/60 flex items-center gap-2">
          <Lock size={12} /> Conexión cifrada · SmartNation S.A.
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-[#475569] hover:text-[#0A2540] mb-8"
            data-testid="back-home-link"
          >
            <ArrowLeft size={14} className="mr-1" /> Volver al inicio
          </Link>

          <div className="text-[10px] uppercase tracking-[0.3em] text-[#475569]">
            Iniciar sesión · Accionista
          </div>
          <h1 className="mt-2 font-heading text-3xl font-bold text-[#0A2540]">
            Bienvenido
          </h1>
          <p className="mt-2 text-sm text-[#475569]">
            Ingrese con su RUT y contraseña para acceder a su perfil.
          </p>

          <form onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label
                htmlFor="identifier"
                className="text-xs uppercase tracking-[0.2em] text-[#475569]"
              >
                RUT
              </Label>
              <Input
                id="identifier"
                data-testid="login-identifier-input"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="12345678-9"
                className="rounded-sm h-11 border-[#E2E8F0]"
                autoComplete="username"
                required
              />
              <p className="text-[11px] text-[#475569]">
                Ingrese sin puntos. En su primer ingreso, la contraseña son los primeros
                4 caracteres de su correo electrónico (en minúsculas).
              </p>
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="text-xs uppercase tracking-[0.2em] text-[#475569]"
              >
                Contraseña
              </Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-sm h-11 border-[#E2E8F0]"
                autoComplete="current-password"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-[#0A2540] hover:bg-[#051626] text-white rounded-sm font-semibold"
            >
              {loading ? "Ingresando…" : "Ingresar"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
